import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { MapPin, User, Clock, RefreshCw, ChevronDown, Package, Trash2 } from 'lucide-react'
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid,
} from 'recharts'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { useWS } from '../contexts/WSContext'
import { useScope } from '../contexts/ScopeContext'
import { ScopeChip } from './MineScopeSelect'
import { equipmentOf } from '../equipment'
import { useConfirm } from './ConfirmDialog'
import ViewState, { EmptyState, LoadingState } from './ViewState'
import i18n from '../i18n'
import MaterialChip from './MaterialChip'
import { CLEAR_CYCLES_CONFIRM, apiError } from '../lib/api'
import { stepLabel } from '../i18n/status'
import { fmtNum, fmtInt, fmtDate, fmtDurShort, fmtClock } from '../i18n/format'

const BRAND = '#017f96'
const TON = '#7c3aed'
// Paleta para os steps do ciclo (barras empilhadas).
const STEP_COLORS = ['#017f96', '#0ea5b7', '#f59e0b', '#8b5cf6', '#ef4444', '#22c55e', '#64748b', '#ec4899']

// O VMC 0.8 fecha no "Finalizar turno" um último ciclo PARCIAL (isFinal): ele aparece na
// lista com selo próprio, mas fica fora de toda contagem, soma e média desta tela.
const isPartial = (c) => c?.isFinal === true

// O 1º ciclo do turno que começou no próprio passo que fecha o ciclo (cycleKind opening,
// VMC 0.8) também é parcial, mas houve carga: conta em ciclos e toneladas e fica fora
// só dos tempos (o backend tira das médias, o gráfico daqui também).
const isOpening = (c) => c?.cycleKind === 'opening'

function CycleMaterial({ cycle }) {
  return (
    <MaterialChip type={cycle.materialType} classification={cycle.materialClassification}
      grading={cycle.materialGrading} material={cycle.material} />
  )
}

const hasMaterial = (c) => !!(c && (c.materialType || (typeof c.material === 'string' && c.material)))

function PartialBadge({ opening = false }) {
  const { t } = useTranslation()
  const key = opening ? 'opening' : 'partial'
  const tone = opening
    ? 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300'
    : 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300'
  return (
    <span title={t(`production:cycles.${key}_hint`)}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium shrink-0 ${tone}`}>
      {t(`production:cycles.${key}`)}
    </span>
  )
}

function fmtT(t) {
  if (t == null || t === 0) return null
  return `${fmtNum(Number(t), { maximumFractionDigits: t < 100 ? 1 : 0 })} ${i18n.t('common:units.tons')}`
}

const fmtSecs = (s) => (s == null ? '—' : fmtDurShort(s))

function fmtRange(startedAt, endedAt) {
  if (!startedAt) return '—'
  const start = new Date(startedAt)
  const day = fmtDate(start, { day: '2-digit', month: 'short' })
  const t1 = fmtClock(start)
  if (!endedAt) return `${day} · ${t1}`
  return `${day} · ${t1}–${fmtClock(endedAt)}`
}

function StepChips({ steps }) {
  const { t, i18n: i18nInst } = useTranslation()
  if (!Array.isArray(steps) || steps.length === 0) return null
  return (
    <div className="flex flex-wrap gap-1.5">
      {steps.map((st, i) => (
        <span key={st.index ?? i}
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: STEP_COLORS[i % STEP_COLORS.length] }} />
          {stepLabel(t, i18nInst, st)} · <span className="tabular-nums text-slate-500 dark:text-slate-400">{fmtSecs(st.seconds)}</span>
        </span>
      ))}
    </div>
  )
}

// Achata os 20 ciclos do device num formato para o gráfico. Os passos agrupam pelo
// código (o rótulo pode vir no idioma da cabine); os ciclos parciais do fim e do início
// de turno ficam de fora, senão a curva despencava no último e no primeiro ponto de cada
// turno.
function buildChart(cycles) {
  const steps = new Map()
  const rows = cycles.filter(c => !isPartial(c) && !isOpening(c)).map((c, i) => {
    const row = {
      label: c.startedAt ? fmtClock(c.startedAt) : `#${i + 1}`,
      total: c.totalSeconds ?? 0,
      tonnage: c.tonnage ?? 0,
    }
    ;(c.steps || []).forEach(st => {
      const key = `s:${st.code || st.label || ''}`
      if (!steps.has(key)) steps.set(key, st)
      row[key] = (row[key] || 0) + (st.seconds || 0)
    })
    return row
  })
  return { rows, stepKeys: [...steps.entries()].map(([key, sample]) => ({ key, sample })) }
}

// O painel expandido descreve O CICLO ABERTO (antes mostrava o último ciclo do device,
// com material, tonelagem e rota de outra volta sob o cabeçalho deste). A busca dos
// últimos 20 ciclos serve só ao gráfico de histórico.
function CycleDetail({ cycle }) {
  const { t, i18n: i18nInst } = useTranslation()
  const deviceName = cycle.deviceName
  const [history, setHistory] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    const ctrl = new AbortController()
    setHistory(null); setError(null)
    fetch(`/api/devices/${encodeURIComponent(deviceName)}/cycles?limit=20`, { signal: ctrl.signal })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
      .then(d => setHistory(Array.isArray(d) ? d : []))
      .catch(err => { if (err.name !== 'AbortError') setError(err.message) })
    return () => ctrl.abort()
  }, [deviceName])

  const { rows, stepKeys } = useMemo(() => buildChart(history || []), [history])
  const latest = cycle
  const hasTon = rows.some(r => r.tonnage > 0)

  return (
    <div className="px-5 pb-5 pt-1 space-y-5">
      {latest && (hasMaterial(latest) || latest.tonnage || latest.loadZone || latest.dumpZone) && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500">{t('common:entities.material')}</p>
            <div className="mt-1 flex min-w-0"><CycleMaterial cycle={latest} />{!hasMaterial(latest) && <span className="text-sm text-slate-400 dark:text-slate-500">—</span>}</div>
          </div>
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500">{t('production:cycles.moved')}</p>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mt-1 tabular-nums">
              {fmtT(latest.tonnage) || '—'}{latest.bcm ? ` · ${fmtInt(Number(latest.bcm))} ${t('common:units.bcm')}` : ''}
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500">{t('production:cycles.loads')}</p>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mt-1 tabular-nums">{latest.loads ?? '—'}</p>
          </div>
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500">{t('production:cycles.route')}</p>
            <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 truncate">{latest.loadZone || '—'} → {latest.dumpZone || '—'}</p>
          </div>
        </div>
      )}

      {latest && Array.isArray(latest.steps) && latest.steps.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">{t('production:cycles.steps_this_cycle')}</p>
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
            {latest.steps.map((st, i) => (
              <div key={st.index ?? i}
                className="flex items-center justify-between px-3 py-2 text-sm border-b border-slate-100 dark:border-slate-800 last:border-0">
                <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300 min-w-0">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: STEP_COLORS[i % STEP_COLORS.length] }} />
                  <span className="truncate">{stepLabel(t, i18nInst, st)}</span>
                </span>
                <span className="tabular-nums text-slate-700 dark:text-slate-200 shrink-0 ml-3">{fmtSecs(st.seconds)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">{t('production:cycles.last_n_cycles', { count: rows.length })}</p>
        {history === null && !error ? (
          <LoadingState label={t('production:cycles.loading_history')} className="h-40" />
        ) : error ? (
          <ViewState error={error} className="h-40" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-400 dark:text-slate-500 py-6 text-center">{t('production:cycles.no_history')}</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
              <YAxis yAxisId="t" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false}
                tickFormatter={(v) => fmtSecs(v)} width={56} />
              {hasTon && (
                <YAxis yAxisId="ton" orientation="right" tick={{ fontSize: 11, fill: TON }} tickLine={false} axisLine={false}
                  tickFormatter={(v) => `${v}t`} width={42} />
              )}
              <Tooltip
                formatter={(v, name) => [name === t('production:cycles.tonnage') ? `${v} ${t('common:units.tons')}` : fmtSecs(v), name]}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {stepKeys.map(({ key, sample }, i) => (
                <Line key={key} yAxisId="t" type="monotone" dataKey={key} name={stepLabel(t, i18nInst, sample)}
                  stroke={STEP_COLORS[i % STEP_COLORS.length]} strokeWidth={2}
                  dot={{ r: 2 }} activeDot={{ r: 4 }} connectNulls />
              ))}
              <Line yAxisId="t" type="monotone" dataKey="total" name={t('production:cycles.total')} stroke={BRAND}
                strokeWidth={2.5} strokeDasharray="5 3" dot={false} />
              {hasTon && (
                <Line yAxisId="ton" type="monotone" dataKey="tonnage" name={t('production:cycles.tonnage')} stroke={TON}
                  strokeWidth={2.5} dot={{ r: 2 }} activeDot={{ r: 4 }} />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}

// A localização mostrada é a do CICLO quando o backend a devolve (mineId/pitName são
// gravados no fecho); só na falta deles, em ciclos antigos, cai no cadastro atual do
// equipamento, e aí a linha diz que é o atual.
function cyclePlace(cycle, eq, mines) {
  const mine = cycle.mineId != null
    ? (mines.find(m => Number(m.id) === Number(cycle.mineId))?.name || null)
    : null
  const pit = cycle.pitName || null
  if (mine || pit) return { text: mine ? (pit ? `${mine} · ${pit}` : mine) : pit, historic: true }
  return { text: eq.mine ? (eq.pit ? `${eq.mine} · ${eq.pit}` : eq.mine) : '—', historic: false }
}

function CycleCard({ cycle, eq, place, expanded, onToggle }) {
  const { t } = useTranslation()
  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md hover:border-slate-300 transition-all overflow-hidden">
      <button onClick={onToggle} className="w-full text-left flex items-center gap-4 px-5 py-4">
        <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-800 shrink-0">
          <span className="equip-icon block w-7 h-7 text-slate-500 dark:text-slate-400"
            style={{ WebkitMaskImage: `url(/equipments/${eq.svg}.svg)`, maskImage: `url(/equipments/${eq.svg}.svg)` }} />
        </div>

        <div className="min-w-0 w-52 shrink-0">
          <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm leading-tight truncate">{eq.name || cycle.deviceName}</p>
          {isPartial(cycle) && <span className="block mt-0.5"><PartialBadge /></span>}
          {!isPartial(cycle) && isOpening(cycle) && <span className="block mt-0.5"><PartialBadge opening /></span>}
          <p className="text-xs text-slate-400 dark:text-slate-500 truncate">{eq.label}</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 truncate flex items-center gap-1 mt-0.5"
            title={place.historic ? t('production:cycles.location_cycle_hint') : t('production:cycles.location_current_hint')}>
            <MapPin size={11} className="shrink-0" />
            {place.text}
            {!place.historic && <span className="italic shrink-0">· {t('production:cycles.location_current')}</span>}
          </p>
        </div>

        <div className="min-w-0 w-40 shrink-0">
          <div className="flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500"><User size={11} /> {t('common:entities.operator')}</div>
          <div className="text-sm text-slate-700 dark:text-slate-200 truncate mt-0.5">{cycle.operatorName || t('production:cycles.no_operator')}</div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500"><Clock size={11} /> {t('production:cycles.period')}</div>
          <div className="text-sm text-slate-700 dark:text-slate-200 truncate mt-0.5 tabular-nums">{fmtRange(cycle.startedAt, cycle.endedAt)}</div>
        </div>

        <div className="min-w-0 w-44 shrink-0">
          <div className="flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500"><Package size={11} /> {t('production:cycles.production')}</div>
          <div className="mt-0.5 flex items-center gap-1.5 min-w-0">
            <CycleMaterial cycle={cycle} />
            {fmtT(cycle.tonnage) && <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 tabular-nums shrink-0">{fmtT(cycle.tonnage)}</span>}
            {!hasMaterial(cycle) && !cycle.tonnage && <span className="text-sm text-slate-400 dark:text-slate-500">—</span>}
          </div>
          {(cycle.loadZone || cycle.dumpZone) && (
            <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate mt-0.5">{cycle.loadZone || '?'} → {cycle.dumpZone || '?'}</p>
          )}
        </div>

        <div className="text-right shrink-0">
          <div className="text-[11px] text-slate-400 dark:text-slate-500">{t('production:cycles.total')}</div>
          <div className="text-base font-semibold tabular-nums" style={{ color: BRAND }}>{fmtSecs(cycle.totalSeconds)}</div>
        </div>

        <ChevronDown size={18} className={`text-slate-300 dark:text-slate-600 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      <div className="px-5 pb-4 -mt-1">
        <StepChips steps={cycle.steps} />
      </div>

      {expanded && (
        <div className="border-t border-slate-100 dark:border-slate-800">
          <CycleDetail cycle={cycle} />
        </div>
      )}
    </div>
  )
}

const PAGE_LIMIT = 100

export default function CyclesView() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { cycleTick, devices, categories, mines } = useWS()
  const { mineQS, scopeLabel } = useScope()
  const [cycles, setCycles] = useState(null)
  const [error, setError] = useState(null)
  const [expanded, setExpanded] = useState(null)
  const [confirmDialog, ask] = useConfirm()
  const abortRef = useRef(null)

  const load = useCallback(() => {
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl
    fetch(`/api/cycles?limit=${PAGE_LIMIT}${mineQS ? '&' + mineQS : ''}`, { signal: ctrl.signal })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
      .then(d => { setCycles(Array.isArray(d) ? d : []); setError(null) })
      .catch(err => { if (err.name !== 'AbortError') { setError(err.message); setCycles([]) } })
  }, [mineQS])

  useEffect(() => {
    load()
    return () => abortRef.current?.abort()
  }, [load])

  // O gatilho de refresh é o frame cycle_done do WSContext, não o feed: pausar os
  // eventos na tela de Eventos congelava esta tela para sempre, sem aviso.
  useEffect(() => { if (cycleTick) load() }, [cycleTick, load])

  // O expurgo agora respeita o escopo de mina e exige a frase que o servidor espera
  // (CLEAR_CYCLES_CONFIRM); sem mina em escopo ele apaga a produção de todas elas, e o
  // diálogo diz isso. O 403 é o ambiente com as ações destrutivas desligadas, que é
  // uma resposta legítima e não uma falha: merece mensagem própria.
  const handleClear = useCallback(() => {
    const scoped = !!mineQS
    ask({
      title: t('production:cycles.clear_title'),
      message: scoped
        ? t('production:cycles.clear_message_scoped', { mine: scopeLabel })
        : t('production:cycles.clear_message'),
      confirmWord: t('production:cycles.clear_word'),
      confirmLabel: t('common:actions.clear'),
      danger: true,
      onConfirm: async () => {
        const res = await fetch(`/api/cycles?${mineQS ? mineQS + '&' : ''}confirm=${encodeURIComponent(CLEAR_CYCLES_CONFIRM)}`,
          { method: 'DELETE' })
        if (res.status === 403) throw new Error(t('production:cycles.clear_disabled'))
        if (!res.ok) throw await apiError(res)
        setCycles([])
      },
    })
  }, [ask, t, mineQS, scopeLabel])

  const complete = useMemo(() => (cycles || []).filter(c => !isPartial(c)), [cycles])
  const partialCount = (cycles || []).length - complete.length
  const totalTon = useMemo(() => complete.reduce((a, c) => a + (c.tonnage || 0), 0), [complete])
  const truncated = (cycles || []).length >= PAGE_LIMIT

  if (cycles === null && !error) return <LoadingState label={t('production:cycles.loading')} className="h-full" />

  return (
    <div className="h-full flex flex-col">
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-3 flex items-center gap-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t('production:cycles.completed', { count: complete.length })}
          {partialCount > 0 && (
            <span className="text-slate-400 dark:text-slate-500" title={t('production:cycles.partial_hint')}> · {t('production:cycles.partial_count', { count: partialCount })}</span>
          )}
        </p>
        <ScopeChip />
        {totalTon > 0 && (
          <div className="flex items-center gap-1.5 text-sm" title={truncated ? t('production:cycles.moved_partial_hint') : undefined}>
            <Package size={14} className="text-violet-500" />
            <span className="font-semibold text-slate-700 dark:text-slate-200 tabular-nums">{fmtInt(totalTon)} {t('common:units.tons')}</span>
            {/* A soma é só dos ciclos carregados nesta página, então o rótulo diz "nos
                últimos N" em vez de sugerir o total do período. */}
            <span className="text-slate-400 dark:text-slate-500">
              {truncated ? t('production:cycles.moved_partial', { n: PAGE_LIMIT }) : t('production:cycles.moved_suffix')}
            </span>
          </div>
        )}
        <div className="ml-auto flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500">
            <RefreshCw size={13} /> {t('production:cycles.subtitle')}
          </span>
          {/* Apagar ciclos destrói histórico de produção: admin (routes.go). */}
          <button
            onClick={handleClear}
            disabled={cycles.length === 0 || !can('admin')}
            title={!can('admin') ? t('admin:perms.needs_admin') : undefined}
            className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border border-status-down/40 text-status-down hover:bg-status-down/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Trash2 size={16} /> {t('common:actions.clear')}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 scrollbar-thin space-y-3">
        {error ? (
          <ViewState error={error} onRetry={load} />
        ) : cycles.length === 0 ? (
          <EmptyState icon={RefreshCw} message={t('production:cycles.empty')} />
        ) : (
          cycles.map(c => {
            // Resolve o equipamento pela lista ao vivo (tem formData → tipo/nome adotado/mina/pit).
            const dev = devices.find(d => d.name === c.deviceName) || { name: c.deviceName }
            const eq = equipmentOf(dev, categories, mines)
            const isOpen = expanded === c.id
            return (
              <CycleCard
                key={c.id}
                cycle={c}
                eq={eq}
                place={cyclePlace(c, eq, mines)}
                expanded={isOpen}
                onToggle={() => setExpanded(isOpen ? null : c.id)}
              />
            )
          })
        )}
      </div>
      {confirmDialog}
    </div>
  )
}
